import Hero from "@/components/Hero";
import StatsBar from "@/components/StatsBar";
import Services from "@/components/Services";
import AICalculator from "@/components/AICalculator";
import References from "@/components/References";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import SEOHead from "@/components/SEOHead";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Award, Clock3, ShieldCheck } from "lucide-react";
import { CITIES, COMPANY, FAQS } from "@/data/content";

export default function Home() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.slice(0, 6).map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="EasyRohbau – Premium Rohbau in Köln & NRW | KI-Preisrechner"
        description="EasyRohbau realisiert hochwertige Rohbauprojekte in Köln, Bonn und NRW. KI-Preisrechner, Festpreisgarantie, 21 Jahre Erfahrung."
        schema={schema}
      />
      <Hero />
      <StatsBar />

      {/* Trust strip / partner logos placeholder */}
      <section className="bg-white border-b border-slate-100" data-testid="trust-strip">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          {[
            { Icon: Award, title: "DIN-zertifiziert", sub: "DIN 1045 / EN 13670" },
            { Icon: ShieldCheck, title: "Voll versichert", sub: "Bauleistung + Haftpflicht" },
            { Icon: Clock3, title: "Termintreu", sub: "98% pünktlich abgeschlossen" },
            { Icon: Award, title: "Festpreisgarantie", sub: "Keine Nachträge" },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center bg-[#F5F5F5] text-[#2873B8] rounded-sm">
                <t.Icon className="w-5 h-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-[#0C2749]" style={{ fontFamily: "Outfit" }}>{t.title}</p>
                <p className="text-xs text-slate-500">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Services />

      {/* Why us / Process */}
      <section className="bg-[#0C2749] text-white" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2C80C8] mb-4">Warum EasyRohbau</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: "Outfit" }}>
              Vier Schritte zu Ihrem <span className="text-[#2C80C8]">fertigen Rohbau.</span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Klar strukturierter Prozess, transparente Kommunikation, persönliche Bauleitung. Sie wissen jederzeit, wo Ihr Projekt steht.
            </p>
            <Link to="/preisrechner" className="btn-shimmer inline-flex items-center gap-2 bg-white text-[#0C2749] hover:bg-[#F5F5F5] px-7 py-4 rounded-sm font-medium transition-colors">
              Preis berechnen <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
            {[
              { n: "01", t: "Erstgespräch", d: "Kostenlos & unverbindlich. Wir hören zu, prüfen Machbarkeit und beraten transparent." },
              { n: "02", t: "Vor-Ort-Besichtigung", d: "Wir schauen uns Grundstück, Pläne und örtliche Gegebenheiten an." },
              { n: "03", t: "Festpreisangebot", d: "Detailliert, transparent, ohne versteckte Nachträge." },
              { n: "04", t: "Umsetzung", d: "Termintreue Ausführung mit fester Bauleitung und Wochen-Reports." },
            ].map((s) => (
              <div key={s.n} className="bg-[#0C2749] p-8 hover:bg-[#0C2749]/80 transition-colors">
                <p className="text-xs text-[#2C80C8] mb-2 tracking-[0.2em]">{s.n}</p>
                <p className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>{s.t}</p>
                <p className="text-sm text-white/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AICalculator />
      <References />
      <Testimonials />

      {/* Local areas */}
      <section className="bg-white" data-testid="local-areas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-4">Ihre Region</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0C2749]" style={{ fontFamily: "Outfit" }}>
              Lokal verankert. <span className="text-[#2873B8]">Persönlich erreichbar.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/standort/${c.slug}`}
                data-testid={`local-link-${c.slug}`}
                className="group bg-[#F5F5F5] hover:bg-[#0C2749] p-8 rounded-sm transition-colors"
              >
                <MapPin className="w-5 h-5 text-[#2873B8] group-hover:text-[#2C80C8] mb-4" />
                <p className="text-2xl font-semibold text-[#0C2749] group-hover:text-white" style={{ fontFamily: "Outfit" }}>Rohbau {c.name}</p>
                <p className="text-xs text-slate-500 group-hover:text-white/70 mt-2">{c.region} · PLZ {c.postal}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQ />

      {/* Contact CTA */}
      <section id="kontakt" className="bg-[#F5F5F5]" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2873B8] mb-4">Kontakt</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0C2749] mb-6" style={{ fontFamily: "Outfit" }}>
              Sprechen wir über Ihr Projekt.
            </h2>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Senden Sie uns eine Anfrage – wir melden uns binnen 24 Stunden mit einer ersten Einschätzung. Persönlich, ehrlich, fair.
            </p>
            <div className="space-y-4">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-3 text-[#0C2749] hover:text-[#2873B8]" data-testid="home-contact-phone">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
              <p className="flex items-center gap-3 text-[#0C2749]">
                <MapPin className="w-4 h-4" /> {COMPANY.address}
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ContactForm source="startseite" />
          </div>
        </div>
      </section>
    </>
  );
}
